import type { OverviewFragment } from '@/lib/types/datocms/graphql/generated'
import Image from 'next/image'
import { StructuredText } from 'react-datocms'
import CtaDefault from '../ui/cta/CtaDefault'
import Eyebrow from '../ui/Eyebrow'
import CircleGraphic from '../ui/CircleGraphic'
import { TextWithHighlight } from '../TextWithHighlight'
import { isStructuredTextEmpty } from '@/utils/datocms/isStructuredTextEmpty'

export default function OverviewSection({
	title,
	image,
	tag,
	content,
}: Readonly<OverviewFragment>) {
	return (
		<section className="overview-section overflow-hidden @container">
			<div className="pt-16 @3xl:py-20">
				<div className="relative container flex flex-col gap-4 @3xl:gap-10">
					{/* Eyebrow */}
					{tag ? <Eyebrow>{tag}</Eyebrow> : null}

					{/* Title */}
					{title && !isStructuredTextEmpty(title.text) && (
						<h2 className="heading-size-1 max-w-3xl @3xl:max-w-6xl ">
							<TextWithHighlight text={title} />
						</h2>
					)}

					<div className="grid grid-cols-2 gap-5 @3xl:gap-8 w-fit absolute top-0 right-0 translate-x-10 @3xl:translate-x-48 -translate-y-32 @3xl:-translate-y-48">
						{Array.from({ length: 4 }).map((_, index) => (
							<CircleGraphic key={`shape-${index + 1}`} />
						))}
					</div>
				</div>

				<div className="flex flex-col @3xl:grid @3xl:grid-cols-2 mt-16 @3xl:mt-32 max-w-screen-2xl mx-auto">
					{/* Primary image */}
					{image?.[0]?.url ? (
						<div className="relative mb-auto">
							<Image
								src={image?.[0]?.url as string}
								alt={image?.[0]?.alt ?? ('Hero Image' as string)}
								width={780}
								height={800}
								className="relative h-full w-full object-cover aspect-square z-10"
								sizes="(max-width: 768px) 100vw, 50vw"
							/>
							<div className="hidden @3xl:flex gap-5 @3xl:gap-8 w-fit absolute bottom-0 right-0 -translate-x-48 translate-y-32">
								{Array.from({ length: 3 }).map((_, index) => (
									<CircleGraphic key={`shape-${index + 1}`} />
								))}
							</div>
						</div>
					) : null}

					{/* Content */}
					<div className="common-x-padding flex flex-col gap-4 @3xl:gap-9 w-full py-16 @3xl:pt-9 @3xl:pb-0 @5xl:max-w-lg mx-auto @3xl:border-t @3xl:border-contrast/10 [&_h3]:text-3xl">
						<div className="tracking-tighter">
							{content?.[0]?.title ? <StructuredText data={content[0].title} /> : null}
						</div>

						{content?.[0]?.description ? (
							<div className="text-lg">
								<StructuredText data={content[0].description} />
							</div>
						) : null}

						{content?.[0]?.cta ? (
							<CtaDefault cta={content[0].cta} className="mt-4 @3xl:mt-3" />
						) : null}

						{content?.[0]?.image?.[0]?.url ? (
							<Image
								src={content[0].image?.[0]?.url as string}
								alt={(content[0].image?.[0]?.alt as string) ?? 'Overview Image'}
								width={content[0].image?.[0]?.width as number}
								height={content[0].image?.[0]?.height as number}
								className="h-full w-full @3xl:w-fit @3xl:min-w-[345px] object-contain mt-16 @3xl:mt-36 @3xl:ml-auto bg-white-frosted"
								sizes="(max-width: 768px) 100vw, 50vw"
							/>
						) : null}
					</div>
				</div>
			</div>
		</section>
	)
}
